import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HOME_CONTENT_MOCK } from '../../data/home-content.mock';
import type { HomeParaMiCard, HomeParaMiContent } from '../../models/home-para-mi-content.model';
import { HomeParaMiService } from '../../services/home-para-mi.service';
import {
  isExternalParaMiUrl,
  parseParaMiInternalLink,
  routerLinkCommandsForInternal,
  type ParaMiInternalLink,
} from '../../utils/home-para-mi-link.util';
import { prepareParaMiTitleHtml } from '../../utils/home-para-mi-html.util';
import { AnalyticsTagService } from '../../../../services/analytics-tag.service';

@Component({
  selector: 'app-home-para-mi',
  templateUrl: './home-para-mi.component.html',
  styleUrls: ['./home-para-mi.component.scss'],
  standalone: false,
})
export class HomeParaMiComponent implements OnInit {
  content: HomeParaMiContent = buildInitialContent();
  titleSafe: SafeHtml = '';

  constructor(
    private readonly homeParaMiService: HomeParaMiService,
    private readonly sanitizer: DomSanitizer,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.applyTitle(this.content.sectionTitle);
  }

  ngOnInit(): void {
    this.homeParaMiService.loadContent().subscribe((content) => {
      this.content = content;
      this.applyTitle(content.sectionTitle);
    });
  }

  isExternalUrl(url: string): boolean {
    return isExternalParaMiUrl(url);
  }

  /** Fragmento interno (#anchor); vacio si no aplica. */
  fragmentOf(url: string): string | undefined {
    const trimmed = (url ?? '').trim();
    if (trimmed.startsWith('#')) {
      return trimmed.slice(1);
    }
    return undefined;
  }

  /** Deep-link interno segun urlForward de BX. */
  internalLinkOf(url: string): ParaMiInternalLink | null {
    return parseParaMiInternalLink(url);
  }

  /** /home → misma ruta; /login u otras → navega a esa pantalla. */
  routerCommandsOf(link: ParaMiInternalLink): string[] {
    return routerLinkCommandsForInternal(link);
  }

  trackParaMiCta(card: 'natural' | 'juridica'): void {
    const isNatural = card === 'natural';
    this.analyticsTag.trackCtaClick({
      ctaLabel: isNatural ? this.content.natural.buttonLabel : this.content.juridica.buttonLabel,
      ctaSection: 'para-mi',
      ctaComponent: 'home-para-mi',
      ctaElementId: isNatural ? 'btn-home-hacer-crecer-negocio' : 'btn-home-potenciar-empresa',
      hu: '8300',
    });
  }

  private applyTitle(title: string): void {
    this.titleSafe = this.sanitizer.bypassSecurityTrustHtml(prepareParaMiTitleHtml(title)); // NOSONAR
  }
}

function buildInitialContent(): HomeParaMiContent {
  const mock = HOME_CONTENT_MOCK.paraMi;
  const toCard = (card: {
    title: string;
    description: string;
    buttonLabel: string;
    buttonUrl: string;
    pasos: readonly string[];
  }): HomeParaMiCard => ({
    title: card.title,
    description: card.description,
    buttonLabel: card.buttonLabel,
    buttonUrl: card.buttonUrl,
    pasos: card.pasos.map((descripcion) => ({ descripcion })),
  });
  return {
    sectionTitle: prepareParaMiTitleHtml(mock.sectionTitle),
    natural: toCard(mock.natural),
    juridica: toCard(mock.juridica),
  };
}
